import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import TransactionHistory from './components/TransactionHistory';
import NotificationModal from '../home/components/NotificationModal';

export default function WalletPage() {
  const navigate = useNavigate();

  const { isAuthenticated, user, updateUserData, logout, notifications } = useAuth();
  const [activeTab, setActiveTab] = useState<'deposit' | 'withdraw'>('deposit');
  const [amount, setAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [transactionLink, setTransactionLink] = useState('');
  const [withdrawalPin, setWithdrawalPin] = useState('');
  const [showWithdrawalPin, setShowWithdrawalPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorTxt, setErrorTxt] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isOpenNotificationDialog, setIsOpenNotificationDialog] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState('trc20');

  const onOpenNotiDlg = () => setIsOpenNotificationDialog(true);
  const onCloseNotiDlg = () => setIsOpenNotificationDialog(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handleDeposit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    let api_url = "https://user-api.u-slot.vip/api/Alarm/charge_req"
    try {
      const response = await fetch(api_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: allows cookies to be sent and received
        body: JSON.stringify({
          amount: amount,
          description: transactionLink,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setShowError(true);
        if (data.message == "exist outstanding requests.") {
          console.log("You have a pending request already.", "error");
          setErrorTxt("You have a pending request already.");
        } else {
          console.log("Request Failed!", "error");
          setErrorTxt("Request Failed! Try again.");
        }
        setLoading(false);
        setTimeout(() => setShowError(false), 6000);
        return
      }

      if (data) {
        if (data.code == 0) {
          console.log("Requested successfully.", "success");
          setShowSuccess(true);
          setAmount('');
        } else {
          setShowError(true);
          setErrorTxt("Request Failed! Try again.");
          console.log("Request Failed!", "error");
        }
        // Close modal and redirect or refresh
        // window.location.href = '/dashboard'; // Or use Next.js router
      }
    } catch (err) {
      console.log(err instanceof Error ? err.message : 'An error ccurred', "error");
      setShowError(true);
      setErrorTxt("Request Failed! Try again.");
    } finally {
      setLoading(false);
    }

    setTimeout(() => setShowSuccess(false), 6000);
    setTimeout(() => setShowError(false), 6000);
  };

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();

    // if (withdrawalPin.length !== 6) {
    //   alert('Withdrawal PIN must be 6 digits.');
    //   return;
    // }

    setLoading(true);

    let api_url = "https://user-api.u-slot.vip/api/Alarm/discharge_req"
    try {
      const response = await fetch(api_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: allows cookies to be sent and received
        body: JSON.stringify({
          amount: amount,
          description: selectedNetwork + ":" + withdrawAddress,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.message == "exist outstanding requests.") {
          console.log("You have a pending request already.", "error");
          setShowError(true);
          setErrorTxt("You have a pending request already.");
        } else {
          console.log("Request Failed!", "error");
          setShowError(true);
          setErrorTxt("Request Failed! Try again.");
        }
        setLoading(false);
        setTimeout(() => setShowError(false), 6000);
        return;
      }

      if (data) {
        if (data.code == 0) {
          console.log("Requested successfully.", "success");
          setShowSuccess(true);
          setAmount('');
          setWithdrawAddress('');
          setTransactionLink('');
          setWithdrawalPin('');
        } else {
          console.log("Request Failed!", "error");
          setShowError(true);
          setErrorTxt("Request Failed! Try again.");
        }
        // Close modal and redirect or refresh
        // window.location.href = '/dashboard'; // Or use Next.js router
      }
    } catch (err) {
      console.log(err instanceof Error ? err.message : 'An error ccurred', "error");
      setShowError(true);
      setErrorTxt(err instanceof Error ? err.message : 'An error ccurred');
    } finally {
      setLoading(false);
    }

    setTimeout(() => setShowSuccess(false), 6000);
    setTimeout(() => setShowError(false), 6000);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handlePresetClick = (preset: number) => {
    const currentAmount = parseFloat(amount) || 0;
    const newAmount = currentAmount + preset;
    setAmount(newAmount.toString());
  };

  useEffect(() => {
    if (activeTab === 'withdraw') {
      setAmount('');
      setWithdrawalPin('');
    }
  }, [activeTab]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0D0E11] via-[#111319] to-[#16181D]">
      {/* Header */}
      <header className="bg-[#1a1c24] border-b border-[#FFD349]/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo & Mobile Balance */}
            <div className="flex flex-col gap-2">
              <Link to="/" className="flex items-center gap-3">
                <h1 className="flex items-center gap-2">
                  <div className="relative w-10 h-10 flex items-center justify-center">
                    <img
                      src="https://public.readdy.ai/ai/img_res/aa29cde3-a1c3-46be-b8a4-d1063c9a3299.jpg"
                      alt="DIGITWISE Logo"
                      className="w-full h-full object-contain drop-shadow-[0_0_8px_rgba(239,191,101,0.4)]"
                    />
                  </div>
                  <span className="text-3xl font-bold bg-gradient-to-r from-[#FFD349] via-[#FFC107] to-[#FF9800] bg-clip-text text-transparent" style={{
                    fontFamily: 'Orbitron, sans-serif'
                  }}>
                    DIGITWISE
                  </span>
                </h1>
              </Link>

              {/* Mobile Balance - Below Logo */}
              {user && (
                <div className="md:hidden flex items-center justify-center gap-1 bg-[#0D0E11] border border-[#FFD349]/20 rounded px-3 py-1.5 mx-auto w-fit">
                  <i className="ri-wallet-3-line text-[#FFD349] text-sm"></i>
                  <span className="text-white font-semibold text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    ${user.balance.toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Home
              </Link>
              <Link to="/wallet" className="text-[#FFD349] text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Wallet
              </Link>
              <Link to="/support" className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Support
              </Link>
              <div className='text-white/70 hover:text-white transition-colors text-sm cursor-pointer' onClick={onOpenNotiDlg}>Notification
                {(notifications > 0) && (<span className="text-xs font-semibold ml-1 text-white bg-red-600 rounded-full"
                  style={{ padding: '2px 6px 3px 6px' }}>
                  {(notifications > 10 ? '10+' : notifications)}
                </span>)}
              </div>
            </nav>

            {/* Desktop Balance & Logout */}
            <div className="hidden md:flex items-center gap-4">
              <div className="flex items-center gap-3 bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-2">
                <i className="ri-wallet-3-line text-[#FFD349] text-lg"></i>
                <div>
                  <p className="text-white/60 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>Balance</p>
                  <p className="text-[#FFD349] font-bold text-lg" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    ${user?.balance.toLocaleString() || '0'}
                  </p>
                </div>
              </div>
              <button
                onClick={logout}
                className="bg-gradient-to-r from-[#FFD349] to-[#FFC107] text-black px-6 py-2 rounded-lg font-bold text-sm hover:shadow-[0px_0px_20px_rgba(255,211,73,0.6)] transition-all whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Logout
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white p-2 hover:text-[#FFD349] transition-colors cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pt-4 border-t border-[#FFD349]/20">
              <nav className="flex flex-col gap-3">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium py-2 px-4 rounded-lg hover:bg-[#0D0E11] cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-home-line mr-2"></i>
                  Home
                </Link>
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium py-2 px-4 rounded-lg hover:bg-[#0D0E11] cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-dashboard-line mr-2"></i>
                  Dashboard
                </Link>
                <Link
                  to="/wallet"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#FFD349] text-sm font-medium py-2 px-4 rounded-lg bg-[#0D0E11] cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-wallet-3-line mr-2"></i>
                  Wallet
                </Link>
                <Link
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium py-2 px-4 rounded-lg hover:bg-[#0D0E11] cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-user-line mr-2"></i>
                  Profile
                </Link>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                  }}
                  className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium py-2 px-4 rounded-lg hover:bg-[#0D0E11] text-left cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-logout-box-line mr-2"></i>
                  Logout
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Balance Display */}
        <div className="bg-gradient-to-r from-[#FFD349] to-[#FFC107] rounded-xl p-8 mb-8 shadow-[0px_0px_30px_rgba(255,211,73,0.3)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-black/70 text-sm mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Current Balance</p>
              <p className="text-5xl font-black text-black" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                ${user.balance.toLocaleString()}
              </p>
              {/* <p className="text-black/70 text-lg mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>{user.currency}</p> */}
            </div>
            <div className="w-24 h-24 bg-black/20 rounded-full flex items-center justify-center">
              <i className="ri-wallet-3-fill text-5xl text-black"></i>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Deposit/Withdraw Form */}
          <div className="lg:col-span-2">
            <div className="bg-[#1a1c24] border border-[#FFD349]/20 rounded-xl p-6">
              {/* Tabs */}
              <div className="flex gap-2 mb-6 bg-[#0D0E11] p-1 rounded-lg">
                <button
                  onClick={() => setActiveTab('deposit')}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${activeTab === 'deposit'
                    ? 'bg-gradient-to-r from-green-400 to-green-500 text-white'
                    : 'text-white/60 hover:text-white'
                    }`}
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-add-circle-line mr-2"></i>
                  Deposit
                </button>
                <button
                  onClick={() => setActiveTab('withdraw')}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${activeTab === 'withdraw'
                    ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white'
                    : 'text-white/60 hover:text-white'
                    }`}
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-subtract-line mr-2"></i>
                  Withdraw
                </button>
              </div>

              {showSuccess && (
                <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-200 text-sm flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <i className="ri-checkbox-circle-fill text-xl"></i>
                  {activeTab === 'deposit' ? 'Deposit completed successfully!' : 'Withdrawal request submitted!'}
                </div>
              )}

              {showError && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <i className="ri-checkbox-circle-fill text-xl"></i>
                  {errorTxt}
                </div>
              )}

              {/* Deposit Form */}
              {activeTab === 'deposit' && (
                <form onSubmit={handleDeposit} className="space-y-6">
                  {/* <div>
                    <label className="block text-sm font-medium text-white/80 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Network
                    </label>
                    <div className="w-full px-4 py-3 bg-[#0D0E11] border border-[#FFD349]/20 rounded-lg text-white text-sm">
                      TRC20 (TRON)
                    </div>
                  </div> */}

                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Amount (USD)
                    </label>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full px-4 py-3 bg-[#0D0E11] border border-[#FFD349]/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD349] text-sm"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                      placeholder="Enter deposit amount"
                      min="10"
                      step="0.01"
                      required
                    />
                    <div className="flex gap-2 mt-3">
                      {[10, 100, 500, 1000, 5000].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => handlePresetClick(preset)}
                          className="flex-1 py-2 bg-[#0D0E11] hover:bg-[#FFD349]/10 border border-[#FFD349]/20 hover:border-[#FFD349]/40 rounded-lg text-white/80 text-sm transition-all whitespace-nowrap cursor-pointer"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                        >
                          ${preset.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-[#FFD349]/10 border border-[#FFD349]/30 rounded-lg p-4">
                    <p className="text-[#FFD349] text-sm mb-2 font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Deposit Address</p>
                    <div>
                      <p className="text-white text-xs font-mono break-all mb-3">
                        USDT/TRC20 TD6hbR3crbFwnJEmXxQNUGwLRnfPDu6AUm
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          const address = "TD6hbR3crbFwnJEmXxQNUGwLRnfPDu6AUm";
                          navigator.clipboard.writeText(address)
                            .then(() => {
                              // Optional: Show success feedback
                              alert("Address copied to clipboard!");
                            })
                            .catch((err) => {
                              console.error("Failed to copy:", err);
                            });
                        }}
                        className="w-full py-2 bg-[#FFD349]/20 hover:bg-[#FFD349]/30 rounded-lg text-[#FFD349] text-sm transition-colors whitespace-nowrap cursor-pointer"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        <i className="ri-file-copy-line mr-2"></i>
                        Copy Address
                      </button>
                    </div>
                    <div className="mt-4">
                      <p className="text-white text-xs font-mono break-all mb-3">
                        USDC, USDT/ERC20-BEP20 0xF599AdcECD3Daa0e4963325d11700976A7675CeC
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          const address = "0xF599AdcECD3Daa0e4963325d11700976A7675CeC";
                          navigator.clipboard.writeText(address)
                            .then(() => {
                              // Optional: Show success feedback
                              alert("Address copied to clipboard!");
                            })
                            .catch((err) => {
                              console.error("Failed to copy:", err);
                            });
                        }}
                        className="w-full py-2 bg-[#FFD349]/20 hover:bg-[#FFD349]/30 rounded-lg text-[#FFD349] text-sm transition-colors whitespace-nowrap cursor-pointer"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        <i className="ri-file-copy-line mr-2"></i>
                        Copy Address
                      </button>
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="transaction-link"
                      className="block text-sm font-medium text-white/80 mb-2"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      Transaction Link
                      <span className="text-red-400 ml-1">*</span>
                    </label>
                    <div className="relative">
                      <input
                        id="transaction-link"
                        type="url"
                        value={transactionLink}
                        onChange={(e) => setTransactionLink(e.target.value)}
                        className="w-full px-4 py-3 pr-10 bg-[#0D0E11] border border-[#FFD349]/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD349] focus:border-transparent text-sm font-mono transition-all"
                        placeholder="https://tronscan.org/#/transaction/..."
                        required
                        aria-label="Transaction link"
                      />
                      {transactionLink && (
                        <button
                          type="button"
                          onClick={() => setTransactionLink('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                          aria-label="Clear transaction link"
                        >
                          <i className="ri-close-circle-line text-lg"></i>
                        </button>
                      )}
                    </div>
                    <p className="mt-1.5 text-xs text-white/50" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Paste the transaction link
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={loading || !amount || !transactionLink}
                    className="w-full bg-gradient-to-r from-green-400 to-green-500 text-white font-bold py-4 rounded-lg hover:shadow-lg hover:shadow-green-400/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {loading ? 'Processing...' : 'Complete Deposit'}
                  </button>
                </form>
              )}

              {/* Withdraw Form */}
              {activeTab === 'withdraw' && (
                <form onSubmit={handleWithdraw} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Network
                    </label>
                    <select
                      value={selectedNetwork}
                      onChange={(e) => setSelectedNetwork(e.target.value)}
                      className="w-full px-4 py-3 bg-[#0D0E11] border border-[#FFD349]/20 rounded-lg text-white text-sm appearance-none cursor-pointer hover:border-[#FFD349]/40 transition-colors"
                      style={{
                        fontFamily: 'Montserrat, sans-serif',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23FFD349'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.75rem center',
                        backgroundSize: '1.25rem'
                      }}
                    >
                      <option value="trc20" className="bg-[#0D0E11] text-white">
                        USDT / TRC20
                      </option>
                      <option value="erc20-bep20" className="bg-[#0D0E11] text-white">
                        USDC, USDT / ERC20-BEP20
                      </option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Withdrawal Address
                    </label>
                    <input
                      type="text"
                      value={withdrawAddress}
                      onChange={(e) => setWithdrawAddress(e.target.value)}
                      className="w-full px-4 py-3 bg-[#0D0E11] border border-[#FFD349]/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD349] text-sm font-mono"
                      placeholder="Enter your USDT wallet address"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Amount (USD)
                    </label>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full px-4 py-3 bg-[#0D0E11] border border-[#FFD349]/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD349] text-sm"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                      placeholder="Enter withdrawal amount"
                      min="10"
                      max={user.balance}
                      step="0.01"
                      required
                    />
                    <p className="text-white/60 text-xs mt-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Available: ${user.balance.toLocaleString()} {/*{user.currency} */}
                    </p>
                  </div>

                  {/* <div>
                    <label className="block text-sm font-medium text-white/80 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Withdrawal PIN (6 digits) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showWithdrawalPin ? 'text' : 'password'}
                        value={withdrawalPin}
                        onChange={(e) => setWithdrawalPin(e.target.value)}
                        className="w-full px-4 py-3 bg-[#0D0E11] border border-[#FFD349]/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD349] text-sm pr-12"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        placeholder="Enter 6-digit PIN"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        inputMode="numeric"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowWithdrawalPin(!showWithdrawalPin)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors cursor-pointer"
                      >
                        <i className={`ri-eye-${showWithdrawalPin ? 'off' : ''}-line text-lg`}></i>
                      </button>
                    </div>
                    <p className="text-white/60 text-xs mt-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Please enter the 6-digit withdrawal PIN you set during registration
                    </p>
                  </div> */}

                  {/* <div className="bg-blue-500/10 border border-blue-400/30 rounded-lg p-4">
                    <p className="text-blue-400 text-sm mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      <i className="ri-information-line mr-2"></i>
                      Withdrawal Information
                    </p>
                    <ul className="text-white/70 text-xs space-y-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      <li>• Minimum withdrawal: 10 USDT</li>
                      <li>• Withdrawal fee: 1 USDT</li>
                      <li>• Processing time: 10-30 minutes</li>
                    </ul>
                  </div> */}

                  <button
                    type="submit"
                    disabled={loading || !amount || !withdrawAddress}
                    className="w-full bg-gradient-to-r from-blue-400 to-blue-500 text-white font-bold py-4 rounded-lg hover:shadow-lg hover:shadow-blue-400/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {loading ? 'Processing...' : 'Submit Withdrawal'}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Transaction History */}
          <TransactionHistory />
        </div>
      </main>
      <NotificationModal isOpen={isOpenNotificationDialog} onClose={onCloseNotiDlg} />
    </div>
  );
}
